import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, resolveFileUrl } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  ArrowLeft, GraduationCap, KeyRound, BookOpen, Calendar as CalendarIcon,
  Phone, Mail, MapPin, BadgeCheck, FileText, Award, Plus, Trash2, Copy,
  Briefcase, AlertCircle, Pencil, Upload, ImageOff, Building2,
} from "lucide-react";
import { toast } from "sonner";

const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export default function TeacherProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [profile, setProfile] = useState(null);
  const [allSubjects, setAllSubjects] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [schoolName, setSchoolName] = useState("");
  const [loading, setLoading] = useState(true);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});

  // Credentials dialog state
  const [credOpen, setCredOpen] = useState(false);
  const [creds, setCreds] = useState(null);

  // Qualifications & certifications dialogs
  const [qualOpen, setQualOpen] = useState(false);
  const [qualForm, setQualForm] = useState({ degree: "", university: "", year: "", country: "" });
  const [certOpen, setCertOpen] = useState(false);
  const [certForm, setCertForm] = useState({ name: "", issuer: "", year: "", expiry: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/teachers/${id}/profile`);
      setProfile(r.data);
      const [subsR, clsR, sched, scR] = await Promise.all([
        api.get(`/subjects?school_id=${r.data.school_id}`),
        api.get(`/classes?school_id=${r.data.school_id}`),
        api.get(`/timetable?teacher_id=${id}`),
        api.get("/schools").catch(() => ({ data: [] })),
      ]);
      setAllSubjects(subsR.data || []);
      setAllClasses(clsR.data || []);
      setSchedule(sched.data || []);
      const sc = (scR.data || []).find(x => x.id === r.data.school_id);
      setSchoolName(sc ? (sc.name_ar || sc.name) : "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const openEdit = () => {
    setForm({
      ...profile,
      emergency_contact: profile.emergency_contact || { name: "", relation: "", phone: "" },
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const payload = { ...form };
      // Remove enriched fields that should not be sent back
      delete payload.account;
      delete payload.subjects_detail;
      delete payload.homeroom_classes_detail;
      delete payload.weekly_slots_count;
      delete payload.classes_taught_count;
      delete payload.created_at;
      delete payload.updated_at;
      await api.put(`/teachers/${id}`, payload);
      toast.success(t("updated_successfully"));
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const createOrResetAccount = async () => {
    try {
      const url = profile?.account
        ? `/teachers/${id}/reset-password`
        : `/teachers/${id}/create-account`;
      const r = await api.post(url);
      setCreds(r.data);
      setCredOpen(true);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t("copied_to_clipboard"));
  };

  const addQualification = async () => {
    if (!qualForm.degree.trim()) {
      toast.error(t("degree_required"));
      return;
    }
    const next = [...(profile.qualifications || []), {
      ...qualForm, year: qualForm.year ? Number(qualForm.year) : null,
    }];
    await api.put(`/teachers/${id}`, { ...profile, qualifications: next });
    setQualForm({ degree: "", university: "", year: "", country: "" });
    setQualOpen(false);
    toast.success(t("added_successfully"));
    load();
  };

  const removeQualification = async (idx) => {
    const next = (profile.qualifications || []).filter((_, i) => i !== idx);
    await api.put(`/teachers/${id}`, { ...profile, qualifications: next });
    toast.success(t("deleted_successfully"));
    load();
  };

  const addCertification = async () => {
    if (!certForm.name.trim()) {
      toast.error(t("certification_name_required"));
      return;
    }
    const next = [...(profile.certifications || []), {
      ...certForm, year: certForm.year ? Number(certForm.year) : null,
    }];
    await api.put(`/teachers/${id}`, { ...profile, certifications: next });
    setCertForm({ name: "", issuer: "", year: "", expiry: "" });
    setCertOpen(false);
    toast.success(t("added_successfully"));
    load();
  };

  const removeCertification = async (idx) => {
    const next = (profile.certifications || []).filter((_, i) => i !== idx);
    await api.put(`/teachers/${id}`, { ...profile, certifications: next });
    toast.success(t("deleted_successfully"));
    load();
  };

  if (loading || !profile) {
    return <Card className="p-12 text-center text-muted-foreground">{t("loading")}…</Card>;
  }

  const initials = (profile.full_name || "").split(" ").slice(0, 2).map((p) => p[0]).join("");

  return (
    <div className="space-y-6">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/teachers")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 me-1 rtl:rotate-180" />
          {t("back_to_teachers")}
        </Button>
        <Button onClick={openEdit} data-testid="edit-teacher-btn">
          <Pencil className="w-4 h-4 me-1" /> {t("edit_profile")}
        </Button>
      </div>

      {/* Hero card */}
      <Card className="p-6 border shadow-none" data-testid="teacher-hero">
        <div className="flex flex-wrap items-start gap-6">
          <div className="w-24 h-24 rounded-2xl bg-primary/10 text-primary text-3xl font-bold grid place-items-center shrink-0">
            {profile.photo_url
              ? <img src={resolveFileUrl(profile.photo_url)} alt={profile.full_name} className="w-full h-full object-cover rounded-2xl" />
              : initials || <GraduationCap className="w-10 h-10" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-semibold truncate">{profile.full_name}</h1>
              {profile.active === false
                ? <Badge variant="destructive">{t("inactive")}</Badge>
                : <Badge className="bg-emerald-500 hover:bg-emerald-500">{t("active")}</Badge>}
            </div>
            <div className="text-muted-foreground mt-1">{profile.specialization || t("no_specialization")}</div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-muted-foreground">
              {schoolName && <span className="flex items-center gap-1.5 font-medium text-foreground" data-testid="teacher-school"><Building2 className="w-3.5 h-3.5 text-primary" /> {schoolName}</span>}
              {profile.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {profile.email}</span>}
              {profile.phone && <span className="flex items-center gap-1.5 font-mono" dir="ltr"><Phone className="w-3.5 h-3.5" /> {profile.phone}</span>}
              {profile.nationality && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {profile.nationality}</span>}
              {profile.employment_type && (
                <span className="flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5" />
                  {t(`employment_${profile.employment_type}`)}
                </span>
              )}
            </div>
          </div>
          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full sm:w-auto sm:max-w-md">
            <Stat icon={BookOpen} value={profile.subjects_detail?.length || 0} label={t("subjects")} />
            <Stat icon={GraduationCap} value={profile.classes_taught_count || 0} label={t("classes_taught")} />
            <Stat icon={CalendarIcon} value={profile.weekly_slots_count || 0} label={t("weekly_slots")} />
            <Stat icon={Award} value={profile.certifications?.length || 0} label={t("certifications")} />
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview"><BadgeCheck className="w-4 h-4 me-1" />{t("overview")}</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="tab-subjects"><BookOpen className="w-4 h-4 me-1" />{t("subjects_and_classes")}</TabsTrigger>
          <TabsTrigger value="qualifications" data-testid="tab-qual"><GraduationCap className="w-4 h-4 me-1" />{t("qualifications")}</TabsTrigger>
          <TabsTrigger value="certifications" data-testid="tab-cert"><Award className="w-4 h-4 me-1" />{t("certifications")}</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule"><CalendarIcon className="w-4 h-4 me-1" />{t("weekly_schedule")}</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account"><KeyRound className="w-4 h-4 me-1" />{t("login_account")}</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4 grid md:grid-cols-2 gap-4">
          <InfoCard title={t("personal_info")} icon={BadgeCheck} rows={[
            [t("national_id"), profile.national_id],
            [t("date_of_birth"), profile.date_of_birth],
            [t("gender"), profile.gender && t(`gender_${profile.gender}`)],
            [t("nationality"), profile.nationality],
            [t("address"), profile.address],
          ]} t={t} />

          <InfoCard title={t("employment_info")} icon={Briefcase} rows={[
            [t("hire_date"), profile.hire_date],
            [t("employment_type"), profile.employment_type && t(`employment_${profile.employment_type}`)],
            [t("experience_years"), profile.experience_years],
            [t("bank_iban"), profile.bank_iban],
          ]} t={t} />

          {profile.emergency_contact?.name && (
            <InfoCard title={t("emergency_contact")} icon={AlertCircle} rows={[
              [t("name"), profile.emergency_contact.name],
              [t("relation"), profile.emergency_contact.relation],
              [t("phone"), profile.emergency_contact.phone],
            ]} t={t} />
          )}

          {profile.bio && (
            <Card className="p-5 border shadow-none md:col-span-2">
              <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-primary" />
                <h3 className="font-semibold">{t("bio")}</h3></div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed text-muted-foreground">{profile.bio}</p>
            </Card>
          )}
        </TabsContent>

        {/* SUBJECTS & CLASSES */}
        <TabsContent value="subjects" className="mt-4 grid md:grid-cols-2 gap-4">
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /> {t("subjects_taught")}</h3>
            {(profile.subjects_detail || []).length === 0
              ? <EmptyHint text={t("no_subjects_assigned")} />
              : (
                <div className="flex flex-wrap gap-2">
                  {profile.subjects_detail.map((s) => (
                    <Badge key={s.id} variant="secondary" className="text-sm py-1.5 px-3">
                      {s.name_ar || s.name}
                    </Badge>
                  ))}
                </div>
              )}
          </Card>
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-primary" /> {t("homeroom_classes")}</h3>
            {(profile.homeroom_classes_detail || []).length === 0
              ? <EmptyHint text={t("no_homeroom_assigned")} />
              : (
                <div className="flex flex-wrap gap-2">
                  {profile.homeroom_classes_detail.map((c) => (
                    <Badge key={c.id} variant="secondary" className="text-sm py-1.5 px-3">
                      {c.name}
                    </Badge>
                  ))}
                </div>
              )}
          </Card>
        </TabsContent>

        {/* QUALIFICATIONS */}
        <TabsContent value="qualifications" className="mt-4">
          <Card className="p-5 border shadow-none">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-primary" /> {t("qualifications")}
              </h3>
              <Button size="sm" onClick={() => setQualOpen(true)} data-testid="add-qual-btn">
                <Plus className="w-4 h-4 me-1" /> {t("add")}
              </Button>
            </div>
            {(profile.qualifications || []).length === 0
              ? <EmptyHint text={t("no_qualifications")} />
              : (
                <div className="space-y-2">
                  {profile.qualifications.map((q, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/30">
                      <div>
                        <div className="font-semibold">{q.degree}</div>
                        <div className="text-sm text-muted-foreground">
                          {[q.university, q.country, q.year].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeQualification(idx)} data-testid={`remove-qual-${idx}`}>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
          </Card>
        </TabsContent>

        {/* CERTIFICATIONS */}
        <TabsContent value="certifications" className="mt-4">
          <Card className="p-5 border shadow-none">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Award className="w-4 h-4 text-primary" /> {t("certifications")}
              </h3>
              <Button size="sm" onClick={() => setCertOpen(true)} data-testid="add-cert-btn">
                <Plus className="w-4 h-4 me-1" /> {t("add")}
              </Button>
            </div>
            {(profile.certifications || []).length === 0
              ? <EmptyHint text={t("no_certifications")} />
              : (
                <div className="space-y-2">
                  {profile.certifications.map((c, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/30">
                      <div className="flex items-center gap-3">
                        <Award className="w-5 h-5 text-amber-500 shrink-0" />
                        <div>
                          <div className="font-semibold">{c.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {[c.issuer, c.year, c.expiry && `${t("expires")}: ${c.expiry}`].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeCertification(idx)} data-testid={`remove-cert-${idx}`}>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
          </Card>
        </TabsContent>

        {/* SCHEDULE */}
        <TabsContent value="schedule" className="mt-4">
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-primary" /> {t("weekly_schedule")}
            </h3>
            {schedule.length === 0
              ? <EmptyHint text={t("no_schedule")} />
              : <TeacherWeeklySchedule slots={schedule} subjects={allSubjects} classes={allClasses} t={t} />}
            <div className="mt-3 text-xs text-muted-foreground">
              <Link to="/timetable" className="text-primary hover:underline">{t("go_to_full_timetable")} →</Link>
            </div>
          </Card>
        </TabsContent>

        {/* ACCOUNT */}
        <TabsContent value="account" className="mt-4">
          <Card className="p-5 border shadow-none" data-testid="account-tab">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-primary" /> {t("login_account")}
            </h3>
            {profile.account ? (
              <div className="space-y-3">
                <Row label={t("email")} value={profile.account.email} mono />
                <Row label={t("role")} value={t(profile.account.role)} />
                <Row label={t("status")} value={
                  profile.account.active
                    ? <Badge className="bg-emerald-500 hover:bg-emerald-500">{t("active")}</Badge>
                    : <Badge variant="destructive">{t("inactive")}</Badge>
                } />
                {profile.account.last_login_at && (
                  <Row label={t("last_login_at")} value={profile.account.last_login_at.slice(0, 19).replace("T", " ")} mono />
                )}
                <div className="pt-2">
                  <Button variant="outline" onClick={createOrResetAccount} data-testid="reset-pwd-btn">
                    <KeyRound className="w-4 h-4 me-1" /> {t("reset_password")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 space-y-3">
                <KeyRound className="w-10 h-10 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">
                  {profile.email ? t("no_account_yet") : t("set_email_first")}
                </p>
                {profile.email && (
                  <Button onClick={createOrResetAccount} data-testid="create-account-btn">
                    <KeyRound className="w-4 h-4 me-1" /> {t("create_login_account")}
                  </Button>
                )}
                {!profile.email && (
                  <Button variant="outline" onClick={openEdit}>
                    <Pencil className="w-4 h-4 me-1" /> {t("add_email_now")}
                  </Button>
                )}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ----------- DIALOGS ----------- */}

      {/* Credentials dialog (shown once after create / reset) */}
      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" />
              {creds?.action === "created" ? t("account_created") : t("password_reset")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200">
              <AlertCircle className="w-4 h-4 inline me-1" />
              {creds?.reset_email_sent
                ? `تم إرسال رابط تعيين كلمة المرور إلى ${creds?.email}. يفتح المعلم الرابط لتعيين كلمته الخاصة (صالح 24 ساعة).`
                : `تعذّر إرسال البريد إلى ${creds?.email}. تحقق من إعدادات البريد الإلكتروني ثم أعد المحاولة.`}
            </div>
            <div className="space-y-2">
              <Label>{t("email")}</Label>
              <div className="flex gap-2">
                <Input value={creds?.email || ""} readOnly className="font-mono" data-testid="cred-email" />
                <Button size="icon" variant="outline" onClick={() => copyToClipboard(creds?.email)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredOpen(false)} data-testid="cred-close">{t("close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("edit_teacher_profile")}</DialogTitle>
          </DialogHeader>
          <EditForm
            form={form} setForm={setForm}
            allSubjects={allSubjects} allClasses={allClasses}
            teacherId={id} onReload={load} t={t}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>{t("cancel")}</Button>
            <Button onClick={saveEdit} data-testid="save-profile-btn">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Qualification dialog */}
      <Dialog open={qualOpen} onOpenChange={setQualOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("add_qualification")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("degree")} *</Label><Input value={qualForm.degree} onChange={(e) => setQualForm({ ...qualForm, degree: e.target.value })} placeholder="بكالوريوس رياضيات" data-testid="qual-degree" /></div>
            <div><Label>{t("university")}</Label><Input value={qualForm.university} onChange={(e) => setQualForm({ ...qualForm, university: e.target.value })} placeholder="جامعة الملك سعود" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("year")}</Label><Input type="number" value={qualForm.year} onChange={(e) => setQualForm({ ...qualForm, year: e.target.value })} placeholder="2018" /></div>
              <div><Label>{t("country")}</Label><Input value={qualForm.country} onChange={(e) => setQualForm({ ...qualForm, country: e.target.value })} placeholder="السعودية" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQualOpen(false)}>{t("cancel")}</Button>
            <Button onClick={addQualification} data-testid="qual-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certification dialog */}
      <Dialog open={certOpen} onOpenChange={setCertOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("add_certification")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("certification_name")} *</Label><Input value={certForm.name} onChange={(e) => setCertForm({ ...certForm, name: e.target.value })} placeholder="شهادة TOEFL" data-testid="cert-name" /></div>
            <div><Label>{t("issuer")}</Label><Input value={certForm.issuer} onChange={(e) => setCertForm({ ...certForm, issuer: e.target.value })} placeholder="ETS" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("year")}</Label><Input type="number" value={certForm.year} onChange={(e) => setCertForm({ ...certForm, year: e.target.value })} placeholder="2023" /></div>
              <div><Label>{t("expiry")}</Label><Input type="date" value={certForm.expiry} onChange={(e) => setCertForm({ ...certForm, expiry: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCertOpen(false)}>{t("cancel")}</Button>
            <Button onClick={addCertification} data-testid="cert-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 border border-border">
      <Icon className="w-5 h-5 text-primary shrink-0" />
      <div>
        <div className="text-xl font-bold leading-none">{value}</div>
        <div className="text-[11px] text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

function InfoCard({ title, icon: Icon, rows, t }) {
  const visible = rows.filter(([_, v]) => v !== null && v !== undefined && v !== "");
  return (
    <Card className="p-5 border shadow-none">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-primary" />
        <h3 className="font-semibold">{title}</h3>
      </div>
      {visible.length === 0 ? (
        <EmptyHint text={t("no_data_filled")} />
      ) : (
        <div className="divide-y divide-border text-sm">
          {visible.map(([k, v]) => (
            <div key={k} className="flex justify-between py-2 gap-3">
              <span className="text-muted-foreground">{k}</span>
              <span className="font-medium text-end">{v}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between text-sm gap-3 py-1.5 border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono" : ""}>{value}</span>
    </div>
  );
}

function EmptyHint({ text }) {
  return <div className="text-center text-sm text-muted-foreground py-6 border border-dashed border-border rounded-lg">{text}</div>;
}

function TeacherWeeklySchedule({ slots, subjects, classes, t }) {
  const subById = Object.fromEntries(subjects.map((s) => [s.id, s]));
  const clsById = Object.fromEntries(classes.map((c) => [c.id, c]));
  // Determine which days actually have slots
  const usedDays = [...new Set(slots.map((s) => s.day))].sort();
  const periods = [...new Set(slots.map((s) => s.period))].sort();
  const grid = {};
  slots.forEach((s) => { grid[`${s.day}-${s.period}`] = s; });
  return (
    <div className="overflow-x-auto">
      <div
        className="grid gap-2 min-w-[500px]"
        style={{ gridTemplateColumns: `60px repeat(${usedDays.length}, minmax(110px, 1fr))` }}
      >
        <div />
        {usedDays.map((d) => (
          <div key={d} className="text-center text-xs font-semibold uppercase tracking-wider p-2 text-muted-foreground">
            {t(DAYS[d])}
          </div>
        ))}
        {periods.map((p) => (
          <PeriodRow key={p} period={p} days={usedDays} grid={grid} subById={subById} clsById={clsById} t={t} />
        ))}
      </div>
    </div>
  );
}

function PeriodRow({ period, days, grid, subById, clsById, t }) {
  return (
    <>
      <div className="grid place-items-center p-1.5 rounded-md bg-muted/30 text-xs font-mono">
        {t("period_short", { n: period })}
      </div>
      {days.map((d) => {
        const s = grid[`${d}-${period}`];
        if (!s) return <div key={d} className="min-h-[62px] rounded-md border-2 border-dashed border-border" />;
        return (
          <div key={d} className="min-h-[62px] rounded-md p-2 text-xs border border-primary/30 bg-primary/5">
            <div className="font-semibold truncate">{subById[s.subject_id]?.name_ar || subById[s.subject_id]?.name || "—"}</div>
            <div className="text-muted-foreground truncate">{clsById[s.class_id]?.name || "—"}</div>
            <div className="text-muted-foreground font-mono text-[10px]">{s.start_time}</div>
          </div>
        );
      })}
    </>
  );
}

function EditForm({ form, setForm, allSubjects, allClasses, teacherId, onReload, t }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setEc = (k, v) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, [k]: v } });
  const [uploading, setUploading] = useState(false);

  const PHOTO_MAX_KB = 150;
  const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("photo_must_be_image"));
      return;
    }
    if (file.size > PHOTO_MAX_KB * 1024) {
      toast.error(t("photo_too_large", { kb: Math.ceil(file.size / 1024), max: PHOTO_MAX_KB }));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/teachers/${teacherId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_url", r.data.photo_url);
      toast.success(t("photo_uploaded"));
      onReload?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && (d.message || d)) || t("error"));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    if (!form.photo_url) return;
    if (!window.confirm(t("delete_photo_confirm"))) return;
    try {
      await api.delete(`/teachers/${teacherId}/photo`);
      set("photo_url", null);
      toast.success(t("deleted_successfully"));
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const toggleSubject = (id) => {
    const cur = form.subjects || [];
    set("subjects", cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };
  const toggleHomeroom = (id) => {
    const cur = form.homeroom_class_ids || [];
    set("homeroom_class_ids", cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  return (
    <Tabs defaultValue="basic" className="mt-2">
      <TabsList>
        <TabsTrigger value="basic">{t("basic_info")}</TabsTrigger>
        <TabsTrigger value="employment">{t("employment_info")}</TabsTrigger>
        <TabsTrigger value="assignments">{t("subjects_and_classes")}</TabsTrigger>
        <TabsTrigger value="bio">{t("bio")}</TabsTrigger>
      </TabsList>

      <TabsContent value="basic" className="mt-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>{t("name")} *</Label><Input value={form.full_name || ""} onChange={(e) => set("full_name", e.target.value)} data-testid="ef-name" /></div>
          <div><Label>{t("specialization")}</Label><Input value={form.specialization || ""} onChange={(e) => set("specialization", e.target.value)} /></div>
          <div><Label>{t("email")}</Label><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="teacher@school.sa" data-testid="ef-email" /></div>
          <div><Label>{t("phone")}</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} dir="ltr" placeholder="+9665…" /></div>
          <div><Label>{t("national_id")}</Label><Input value={form.national_id || ""} onChange={(e) => set("national_id", e.target.value)} dir="ltr" /></div>
          <div><Label>{t("date_of_birth")}</Label><Input type="date" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} /></div>
          <div>
            <Label>{t("gender")}</Label>
            <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">{t("gender_male")}</SelectItem>
                <SelectItem value="female">{t("gender_female")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>{t("nationality")}</Label><Input value={form.nationality || ""} onChange={(e) => set("nationality", e.target.value)} placeholder="السعودية" /></div>
        </div>
        <div><Label>{t("address")}</Label><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
        <fieldset className="border border-border rounded-lg p-4">
          <legend className="text-sm font-semibold px-2">{t("emergency_contact")}</legend>
          <div className="grid sm:grid-cols-3 gap-3 mt-2">
            <div><Label>{t("name")}</Label><Input value={form.emergency_contact?.name || ""} onChange={(e) => setEc("name", e.target.value)} /></div>
            <div><Label>{t("relation")}</Label><Input value={form.emergency_contact?.relation || ""} onChange={(e) => setEc("relation", e.target.value)} placeholder="زوج / أب / أم" /></div>
            <div><Label>{t("phone")}</Label><Input value={form.emergency_contact?.phone || ""} onChange={(e) => setEc("phone", e.target.value)} dir="ltr" /></div>
          </div>
        </fieldset>
      </TabsContent>

      <TabsContent value="employment" className="mt-4 grid sm:grid-cols-2 gap-3">
        <div><Label>{t("hire_date")}</Label><Input type="date" value={form.hire_date || ""} onChange={(e) => set("hire_date", e.target.value)} /></div>
        <div>
          <Label>{t("employment_type")}</Label>
          <Select value={form.employment_type || ""} onValueChange={(v) => set("employment_type", v)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="full_time">{t("employment_full_time")}</SelectItem>
              <SelectItem value="part_time">{t("employment_part_time")}</SelectItem>
              <SelectItem value="contract">{t("employment_contract")}</SelectItem>
              <SelectItem value="substitute">{t("employment_substitute")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>{t("experience_years")}</Label><Input type="number" value={form.experience_years || ""} onChange={(e) => set("experience_years", e.target.value ? Number(e.target.value) : null)} /></div>
        <div><Label>{t("bank_iban")}</Label><Input value={form.bank_iban || ""} onChange={(e) => set("bank_iban", e.target.value)} dir="ltr" placeholder="SA00…" /></div>
        <div className="sm:col-span-2">
          <Label>{t("status")}</Label>
          <Select value={form.active === false ? "false" : "true"} onValueChange={(v) => set("active", v === "true")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">{t("active")}</SelectItem>
              <SelectItem value="false">{t("inactive")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </TabsContent>

      <TabsContent value="assignments" className="mt-4 space-y-4">
        <div>
          <Label className="mb-2 block">{t("subjects_taught")}</Label>
          <div className="flex flex-wrap gap-2">
            {allSubjects.map((s) => {
              const on = (form.subjects || []).includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggleSubject(s.id)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition
                    ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/40"}`}
                >
                  {s.name_ar || s.name}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <Label className="mb-2 block">{t("homeroom_classes")}</Label>
          <div className="flex flex-wrap gap-2">
            {allClasses.map((c) => {
              const on = (form.homeroom_class_ids || []).includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleHomeroom(c.id)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition
                    ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/40"}`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
      </TabsContent>

      <TabsContent value="bio" className="mt-4 space-y-3">
        <div>
          <Label>{t("bio")}</Label>
          <Textarea
            value={form.bio || ""}
            onChange={(e) => set("bio", e.target.value)}
            rows={6}
            placeholder={t("bio_placeholder")}
          />
        </div>
        <div>
          <Label>{t("profile_photo")}</Label>
          <div className="flex items-start gap-4 mt-2 p-4 rounded-lg border border-dashed border-border">
            {/* Preview */}
            <div className="w-24 h-24 rounded-xl bg-muted/40 border border-border grid place-items-center overflow-hidden shrink-0">
              {form.photo_url ? (
                <img
                  src={resolveFileUrl(form.photo_url)}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImageOff className="w-7 h-7 text-muted-foreground" />
              )}
            </div>
            {/* Controls */}
            <div className="flex-1 space-y-2">
              <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md border border-border hover:bg-muted/50 transition text-sm" data-testid="upload-photo-btn">
                <Upload className="w-4 h-4" />
                {uploading ? t("uploading") + "…" : (form.photo_url ? t("replace_photo") : t("choose_photo"))}
                <input
                  type="file"
                  accept={ACCEPT}
                  onChange={onPickPhoto}
                  disabled={uploading}
                  className="hidden"
                  data-testid="photo-input"
                />
              </label>
              {form.photo_url && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={removePhoto}
                  className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 ms-2"
                  data-testid="remove-photo-btn"
                >
                  <Trash2 className="w-4 h-4 me-1" /> {t("remove_photo")}
                </Button>
              )}
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t("photo_upload_hint", { max: PHOTO_MAX_KB })}
              </p>
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}
